import { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useQueryConfiguration } from "../../../Common/constants";
import { detachSensorById, fetchSensorList } from "../../../api";
import { checkForAPISuccess, highlightSearchText, showErrorToast, showSuccessToast } from "../../../Common/commonFunctions";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button, Card, Modal } from "react-bootstrap";
import { ClipLoader } from 'react-spinners';
import _ from "lodash";
import ToolTip from "../Common/Tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClockRotateLeft } from "@fortawesome/free-solid-svg-icons";
import SensorHistory from "./SensorHistory";
import { Icon } from '@iconify/react';
import { convertStringDateToLocalTimestamp } from './../../../Common/dateFormater';

const SensorListTable = props => {

    const ALL = "ALL", DETACHED = "DETACHED", ATTACHED = "ATTACHED";

    const columnKeys = Object.keys(props?.columns);

    const [utils, setUtils] = useState({
        sensorList: [],
        sensorListInitial: [],
        selectedSensorStateFilter: ALL,
        selectedSearchField: ALL,
        searchValue: "",
        selectedIdForSensorHistory: "",
        sensorOnDetach: ""
    });

    const filterSensorByState = (filteredSensors) => {
        filteredSensors = filteredSensors.filter(sensor => {
            if(utils.selectedSensorStateFilter === DETACHED) {
                return _.isEmpty(sensor?.componentName);
            }
            else if(utils.selectedSensorStateFilter === ATTACHED) {
                return !_.isEmpty(sensor?.componentName);
            }
            return true;
        });
        return filteredSensors;
    }

    const checkIfSensorMatchesSearchKey = (sensor) => {
        if(utils.selectedSearchField === ALL) {
            let isMatchingSensor = false;
            for (const column of columnKeys) {
                if (!_.isEmpty(sensor[column]) && sensor[column].toLowerCase().includes(utils.searchValue.toLowerCase())) {
                    isMatchingSensor = true;
                    break;
                }
            }
            return isMatchingSensor;
        }
        return !_.isEmpty(sensor[utils.selectedSearchField]) && sensor[utils.selectedSearchField].toLowerCase().includes(utils.searchValue.toLowerCase());
    }

    const returnSearchBasedSensors = (filteredSensors) => {
        filteredSensors = filteredSensors.filter(
            sensor => checkIfSensorMatchesSearchKey(sensor)
        );
        return filteredSensors;
    }

    const cellBody = (row, field) => {
        if(_.isEmpty(row[field])) {
            return "-";
        }
        if(utils.selectedSearchField === field || utils.selectedSearchField === ALL) {
            return <span dangerouslySetInnerHTML={{ __html: highlightSearchText(row[field], utils.searchValue) }}></span>
        }
        return <span>{row[field]}</span>
    };

    const getSensorStateButtonClass = (state) =>
        `${fetchSensorListQuery.isFetching ? "disable-click" : ""} ` +
        `m-2 p-2 ${state === utils.selectedSensorStateFilter ? "selected" : "unselected"}-sensor-state-btn ` +
        `
            ${
                (state === DETACHED && utils.selectedSensorStateFilter !== DETACHED) ?
                    "sensor-list-btn-detached"
                        :
                    (
                        (state === ATTACHED && utils.selectedSensorStateFilter !== ATTACHED) ?
                            "sensor-list-btn-attached"
                                :
                            ""
                    )
            }
        `;

    const getColumn = (field, header, headerClass, isSortable) =>
        <Column
            sortable={isSortable}
            body={(row) => cellBody(row, field)}
            headerClassName={headerClass}
            field={field}
            header={header}
        />

    const getFilteredSensorList = (filteredSensors) => {
        if(!_.isEmpty(utils.searchValue)) {
            filteredSensors = returnSearchBasedSensors(filteredSensors);
        }
        if(utils.selectedSensorStateFilter !== ALL) {
            filteredSensors = filterSensorByState(filteredSensors)
        }

        return filteredSensors;
    }

    const detachSensor = async () => {
        const { data } = await detachSensorById(utils.sensorOnDetach);
        return data;
    }

    const detachSensorMutation = useMutation(detachSensor);

    const fetchSensors = async () => {
        const { data } = await fetchSensorList(!_.isEmpty(props?.profileId) ? props.profileId : "");
        return data;
    }

    const fetchSensorListQuery = useQuery('fetch-sensor-list', fetchSensors, { ...useQueryConfiguration });

    useEffect(() => {
        if(fetchSensorListQuery.isFetched) {
            if(fetchSensorListQuery.isSuccess && checkForAPISuccess(fetchSensorListQuery?.data?.statusCode)) {
                setUtils({
                    ...utils,
                    sensorList: fetchSensorListQuery?.data?.data,
                    sensorListInitial: fetchSensorListQuery?.data?.data
                })
            }
            else {
                showErrorToast("Failed to fetch sensors");
            }
            fetchSensorListQuery.remove();
        }
    }, [fetchSensorListQuery.isFetched]);

    useEffect(() => {
        if(detachSensorMutation.isSuccess) {
            const data = detachSensorMutation?.data?.data;
            let originalSensorList = _.cloneDeep(utils.sensorListInitial);
            const detachedSensor = originalSensorList.splice(_.findIndex(utils.sensorListInitial, (sensor) => sensor.sensorId === utils.sensorOnDetach), 1)?.[0];

            Object.keys(detachedSensor).forEach(key => {
                detachedSensor[key] = "";
            });
            detachedSensor["sensorId"] = utils.sensorOnDetach;
            detachedSensor["modifiedBy"] = data?.detachedBy;
            detachedSensor["modifiedDateTime"] = data?.detachedAt;

            originalSensorList.unshift(_.clone(detachedSensor));

            let currentlyDisplayedSensors = _.cloneDeep(originalSensorList);
            currentlyDisplayedSensors = getFilteredSensorList(currentlyDisplayedSensors);

            setUtils({ ...utils, sensorOnDetach: "", sensorListInitial: originalSensorList, sensorList: currentlyDisplayedSensors });

            detachSensorMutation.reset();
            showSuccessToast("Sensor detached successfully");
        }
    }, [detachSensorMutation.isSuccess]);

    useEffect(() => {
        fetchSensorListQuery.refetch();
    }, []);

    useEffect(() => {
        setUtils({ ...utils, sensorList: getFilteredSensorList(_.cloneDeep(utils.sensorListInitial)) });
    }, [utils.searchValue, utils.selectedSensorStateFilter, utils.selectedSearchField]);

    return (
        <>
            <div className="w-100">
                <Card className="w-100 mx-1">
                    <div className="py-1 flex-space-even w-100">
                        <div className="w-100 h-100">
                            <Button onClick={() => setUtils({ ...utils, selectedSensorStateFilter: ALL})} className={getSensorStateButtonClass(ALL)}>Show all</Button>
                            <Button onClick={() => setUtils({ ...utils, selectedSensorStateFilter: DETACHED})} className={getSensorStateButtonClass(DETACHED)}>Show Detached</Button>
                            <Button onClick={() => setUtils({ ...utils, selectedSensorStateFilter: ATTACHED})} className={getSensorStateButtonClass(ATTACHED)}>Show Attached</Button>
                        </div>
                        <div className="mt-2 mr-2 sensor-list-search-box-container h-100">
                            <span>Search By</span>
                            <select
                                onChange={(e) => setUtils({ ...utils, selectedSearchField: e.target.value, searchValue: "" })}
                                disabled={fetchSensorListQuery.isFetching}
                                className="ml-2 mr-3 sensor-list-field-select"
                            >
                                <option value={ALL}>All fields</option>
                                {
                                    Object.keys(props?.columns).map(key => 
                                        <option key={key} value={key}>{props?.columns[key]}</option>
                                    )
                                }
                            </select>
                            <input
                                value={utils.searchValue}
                                disabled={fetchSensorListQuery.isFetching}
                                placeholder="Search"
                                className="sensor-list-search-box"
                                onChange={(e) => setUtils({ ...utils, searchValue: e.target.value })}
                            />
                        </div>
                    </div>
                    <Card.Body className="my-0 p-0 mx-2 h-100">
                        <DataTable
                            emptyMessage={<b className="f-17">NO SENSOR(S) FOUND</b>}
                            columnResizeMode="expand"
                            resizableColumns
                            loading={fetchSensorListQuery.isFetching}
                            loadingIcon={<ClipLoader color="#075978" />}
                            cellClassName="data-table-cell"
                            rowClassName="data-table-row"
                            tableClassName="sensor-table"
                            paginator
                            rows={15}
                            paginatorClassName="my-2"
                            value={utils.sensorList}
                        >
                            <Column
                                headerClassName="data-table-header"
                                header="Action"
                                body={(row) =>
                                    <div className="data-table-action-column">
                                        <ToolTip
                                            toolTipKey="sensor-history"
                                            toolTipValue="View sensor history"
                                            overlayComponent={
                                                <FontAwesomeIcon
                                                    onClick={() => setUtils({ ...utils, selectedIdForSensorHistory: row.sensorId })}
                                                    icon={faClockRotateLeft}
                                                />
                                            }
                                        />
                                        {
                                            !_.isEmpty(row?.componentName) &&
                                                <ToolTip
                                                    toolTipKey="sensor-detach"
                                                    toolTipValue="Detach sensor"
                                                    overlayComponent={
                                                        <Icon
                                                            icon={"feather-minus-circle"}
                                                            width={16}
                                                            height={16}
                                                            onClick={() => setUtils({ ...utils, sensorOnDetach: row?.sensorId })}
                                                        />
                                                    }
                                                />
                                        }
                                    </div>
                                }
                            />
                            <Column
                                body={(row) => cellBody(row, "sensorId")}
                                bodyClassName={(row) => !_.isEmpty(row[0]?.componentName) ? "color-green" : "color-red"}
                                headerClassName="data-table-header"
                                field="sensorId"
                                header="Sensor"
                            />
                            { columnKeys.includes("customerName") && getColumn("customerName", "Customer", "data-table-header", true) }
                            { columnKeys.includes("profileName") && getColumn("profileName", "Profile", "data-table-header", true) }
                            { columnKeys.includes("equipmentName") && getColumn("equipmentName", "Equipment", "data-table-header") }
                            { columnKeys.includes("equipmentUnitName") && getColumn("equipmentUnitName", "Equipment Unit", "data-table-header") }
                            { columnKeys.includes("componentName") && getColumn("componentName", "Component", "data-table-header") }
                            { columnKeys.includes("sensorInstallLocation") && getColumn("sensorInstallLocation", "Location", "data-table-header") }
                            { columnKeys.includes("modifiedBy") && getColumn("modifiedBy", "Modified By", "data-table-header", true) }
                            <Column
                                body={(row) => convertStringDateToLocalTimestamp(row.modifiedDateTime, 'MM-dd-YYYY HH:MM')}
                                headerClassName="data-table-header"
                                field="modifiedDateTime"
                                header="Last Modified At"
                                sortable
                            />
                        </DataTable>
                    </Card.Body>
                </Card>
            </div>
            {
                !_.isEmpty(utils.selectedIdForSensorHistory) &&
                    <SensorHistory
                        close={() => setUtils({ ...utils, selectedIdForSensorHistory: "" })}
                        sensorId={utils.selectedIdForSensorHistory}
                    />
            }
            {
                !_.isEmpty(utils.sensorOnDetach) &&
                    <Modal show backdrop="static" onHide={() => setUtils({ ...utils, sensorOnDetach: "" })}>
                        <Modal.Header closeButton>
                            <b>Detach Sensor</b>
                        </Modal.Header>
                        <Modal.Body>
                            <center className="my-5">
                                {
                                    detachSensorMutation.isLoading ?
                                        <ClipLoader color="#075978" />
                                            :
                                        <b>Are you sure to detach the sensor?</b>
                                }
                            </center>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button disabled={detachSensorMutation.isLoading} className="secondary-button" onClick={() => setUtils({ ...utils, sensorOnDetach: "" })}>No</Button>
                            <Button disabled={detachSensorMutation.isLoading} onClick={() => detachSensorMutation.mutate()}>Yes</Button>
                        </Modal.Footer>
                    </Modal>
            }
        </>
    );
}

export default SensorListTable;